import Autoscribe from '@assets/images/autoscribe.svg?react'
import Banner from '@components/buttons/banner.button'
import {
  classNames,
  getTrialExpirationWording,
  formatSecondsToTime,
  getTrialExpirationSubscriptionInDays,
  hasNoSubscription,
  isTrialActive,
} from '@components/utils/helper'
import { Endpoints } from '@constants/endpoints.constant'
import { Methods } from '@constants/methods.constant'
import { navigation, settingsNavigation, getUserNavigation } from '@constants/navigation.constant'
import { Pages } from '@constants/routes.constant'
import { Dialog, Menu, MenuItem, MenuItems, Transition, TransitionChild } from '@headlessui/react'
import Bars3Icon from '@heroicons/react/24/outline/Bars3Icon'
import UserIcon from '@heroicons/react/24/outline/UserCircleIcon'
import { Trans } from '@lingui/macro'
import requestApi from '@services/http-api.service'
import { localStorage } from '@services/local-storage.service'
import { Fragment, useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@contexts/auth.context'
import { useUser } from '@contexts/user.context'
import { useTags } from '@contexts/tags.context'
import { defaultLocale, loadMessages } from '../../i18n'
import { useLoading } from '@contexts/loading.context'

export default function TopNavigationBar({ Routes }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { authTokens } = useAuth()
  const { user, setUser } = useUser()
  const { setTags } = useTags()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { startLoading, stopLoading } = useLoading()

  const trialExpirationSubscriptionInDays = getTrialExpirationSubscriptionInDays(user)

  const updatedNavigation = navigation?.map((item) => ({
    ...item,
    current: item.href === location.pathname,
  }))
  const updatedUserNavigation = getUserNavigation(user)?.map((item) => ({
    ...item,
    current: item.href === location.pathname,
  }))
  const updatedSettingsNavigation = settingsNavigation?.map((item) => ({
    ...item,
    current: item.href === location.pathname,
  }))
  const currentMenuItem = [...updatedNavigation, ...updatedUserNavigation, ...updatedSettingsNavigation].find((item) => item.current)

  const handleSignout = async () => {
    await requestApi({
      endpoint: `${Endpoints.AUTH}`,
      method: Methods.DELETE,
      headers: { Authorization: `Bearer ${authTokens?.refreshToken}` },
    })
    localStorage.removeItem('authTokens')
    localStorage.removeItem('user')
    window.open(Pages.ROOT, '_self')
  }

  const handleNavigation = (item) => {
    if (item.key === 'admin') {
      startLoading()
      // Stop loading after a delay to simulate loading time
      setTimeout(() => {
        stopLoading()
      }, 500)
    }
  }

  useEffect(() => {
    if (sidebarOpen) {
      setSidebarOpen(false)
    }
  }, [location])

  useEffect(() => {
    const fetchData = async () => {
      if (!authTokens?.accessToken || !user?.id) return

      try {
        // Fetch user data
        const responseUser = await requestApi({
          endpoint: `${Endpoints.USERS}/me`,
          method: Methods.GET,
          headers: { Authorization: `Bearer ${authTokens?.accessToken}` },
        })
        setUser(responseUser.data)
        loadMessages(responseUser.data.settings.language || defaultLocale)

        // Only fetch tags now
        const responseTags = await requestApi({
          endpoint: `${Endpoints.TAGS}`,
          method: Methods.GET,
          query: { userId: user?.id },
          headers: { Authorization: `Bearer ${authTokens?.accessToken}` },
        })

        setTags(responseTags.data)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
  }, [authTokens?.accessToken])

  return (
    <>
      {isTrialActive(user) && (
        <Banner>
          <Link to={Pages.BILLING_SETTINGS}>
            <Trans>
              Your free trial will end {getTrialExpirationWording(trialExpirationSubscriptionInDays)} and you have a remaining free consumption of{' '}
              {formatSecondsToTime(user?.trialConsumption || 0)}
            </Trans>
            <br />
            <Trans>Click here to manage your subscription</Trans>
          </Link>
        </Banner>
      )}

      {hasNoSubscription(user) && (
        <Banner>
          <Link to={Pages.BILLING_SETTINGS}>
            <Trans>You are not currently subscribed to a plan. Click here to view the different plans.</Trans>
          </Link>
        </Banner>
      )}

      <div>
        <Transition show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
            <TransitionChild
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 backdrop-blur-sm" />
            </TransitionChild>

            <div className="fixed inset-0 flex">
              <TransitionChild
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="ring-blue flex flex-col gap-y-5 bg-white px-6 ring-1">
                    <div className="flex h-16 shrink-0 items-center">
                      <NavLink to="/">
                        <Autoscribe className="mt-4 h-12 w-auto ring-0" />
                      </NavLink>
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {updatedNavigation?.map((item, index) => (
                              <li key={`${item.key}-${index}`}>
                                <NavLink
                                  to={item.href}
                                  className={classNames(
                                    item.current ? 'bg-lavande' : '',
                                    'group hover:bg-lavande flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                                  )}
                                  target={item.target}
                                >
                                  {item.name}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </li>
                      </ul>
                      <div className="border-blue border-t pt-4 pb-3">
                        <div className="flex items-center px-5">
                          <div className="shrink-0">
                            {user?.picture ? (
                              <img className="size-12 rounded-full" src={user?.picture} alt="profile-picture" />
                            ) : user?.firstName ? (
                              <p className="bg-blue flex size-12 items-center justify-center rounded-full text-center font-semibold text-white">
                                {user?.firstName[0]}
                              </p>
                            ) : (
                              <UserIcon className="text-darkgray size-12 rounded-full" />
                            )}
                          </div>
                          <div className="ml-3">
                            {user?.firstName && user?.lastName && <div className="text-sm font-medium">{`${user.firstName} ${user.lastName}`}</div>}
                            <div className="text-gray-500 text-xs font-medium">{user?.email}</div>
                          </div>
                        </div>
                        <div className="mt-3 space-y-1 px-2">
                          {updatedUserNavigation?.map((item, index) => (
                            <NavLink
                              key={`${item.name}-${index}`}
                              to={item.href}
                              className="block rounded-md px-3 py-2 text-base font-medium"
                              onClick={
                                item.key === 'signout'
                                  ? (e) => {
                                      e.preventDefault()
                                      handleSignout()
                                    }
                                  : () => {}
                              }
                            >
                              {item.name}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </nav>
                  </div>
                </Dialog.Panel>
              </TransitionChild>
            </div>
          </Dialog>
        </Transition>

        {/* Header version desktop */}
        <div className="hidden justify-center bg-white shadow-md lg:sticky lg:inset-x-0 lg:top-0 lg:z-10 lg:flex">
          <div className="flex grow items-center justify-between px-4">
            <div className="flex items-center">
              <NavLink to="/">
                <Autoscribe className="my-3 h-16 w-auto" />
              </NavLink>
              <ul role="list" className="flex gap-x-5 pl-5">
                {updatedNavigation?.map((item, index) => (
                  <li key={`${item.key}-${index}`}>
                    <NavLink to={item.href} target={item.target} className="group hover:text-blue relative px-3 py-4 pb-2 leading-6">
                      {item.name}
                      {/*Ajoute la barre bleue en dessous de l'item actuel*/}
                      {item.current && <span className="bg-blue absolute bottom-0 left-3 h-1 w-1/2 rounded"></span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
            {/* Profile dropdown */}
            <div
              className="relative ml-3 flex h-full items-center"
              onMouseEnter={() => setIsMenuOpen(true)}
              onMouseLeave={() => setIsMenuOpen(false)}
            >
              <div className="flex h-full cursor-pointer items-center">
                {user?.firstName && user?.lastName && (
                  <div className="mr-3 text-right">
                    <div className="text-sm font-medium">{`${user?.firstName} ${user?.lastName}`}</div>
                    <div className="text-gray-400 text-xs font-medium">{user?.email}</div>
                  </div>
                )}
                <div className="shrink-0">
                  {user?.picture ? (
                    <img className="size-10 rounded-full" src={user?.picture} alt="profile-picture" />
                  ) : user?.firstName ? (
                    <p className="bg-blue flex size-10 items-center justify-center rounded-full text-center font-semibold text-white">
                      {user?.firstName[0]}
                    </p>
                  ) : (
                    <UserIcon className="text-darkgray size-10 rounded-full" />
                  )}
                </div>
              </div>
              <Transition
                show={isMenuOpen}
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu as="div" className="absolute top-[calc(100%-8px)] right-0 z-20 w-48 origin-top-right">
                  <MenuItems static className="rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                    {updatedUserNavigation?.map((item) => (
                      <MenuItem key={item.href}>
                        {({ active }) => (
                          <NavLink
                            onClick={
                              item.key === 'signout'
                                ? (e) => {
                                    e.preventDefault()
                                    handleSignout()
                                  }
                                : () => handleNavigation(item)
                            }
                            to={item.href}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'hover:gray-300 hover:bg-gray-100 flex flex-row px-4 py-2 text-sm text-gray-500',
                            )}
                          >
                            {item.icon && <item.icon className={classNames('h-6 w-6 shrink-0', 'mr-3')} aria-hidden="true" />}
                            {item.name}
                          </NavLink>
                        )}
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </Transition>
            </div>
          </div>
        </div>

        {/* Header version mobile */}
        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white p-4 shadow-sm sm:px-6 lg:hidden">
          <button type="button" className="-m-2.5 p-2.5 lg:hidden" onClick={() => setSidebarOpen(true)}>
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="size-6" aria-hidden="true" />
          </button>
          <div className="flex-1 text-sm leading-6 font-semibold">
            {currentMenuItem ? (
              currentMenuItem?.key?.includes('settings') ? (
                <Trans>Settings</Trans>
              ) : (
                currentMenuItem.name
              )
            ) : (
              <Trans>Your conversation</Trans>
            )}
          </div>
        </div>

        <main>{Routes}</main>
      </div>
    </>
  )
}
